import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  FileInput,
  Grid,
  Select,
  TextInput,
  useMantineColorScheme,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  IconCalendar,
  IconCaretDownFilled,
  IconFileSearch,
} from "@tabler/icons-react";
import { generateTimeSeries, useCurrency, useProjectDateFormat } from "../../../utils/helpers";
import MGAAPI from "../../../api/index";
import fileDownload from "js-file-download";
import "./DynamicTariffImport.css";
import displayError from "../../../constants/utils";
import TimeSeriesGraph from "../../timeSeriesGraph";

const MGA_API = new MGAAPI();

const ALLOWED_EXTENSIONS = [".xls", ".xlsx", ".csv"];

const isValidFile = (file) => {
  if (!file) return true;

  return ALLOWED_EXTENSIONS.some((ext) =>
    file.name.toLowerCase().endsWith(ext)
  );
};

const DynamicTariffImport = ({
  importFormData,
  handleDynamicImportChange,
  dynamicGraphData,
  setDynamicGraphData,
  externalErrors,
  getFieldAttributes,
  onChange
}) => {
  const { t } = useTranslation();
  const projectDateFormat = useProjectDateFormat();
  const { colorScheme } = useMantineColorScheme();
  const currency = useCurrency();
  const today = new Date();
  const oneYearAgo = new Date(today);
  oneYearAgo.setFullYear(today.getFullYear() - 1);
  const minStartDate = new Date(today.getFullYear() - 1, 0, 1); // Jan 1 of previous year
  
  // Handle Invalid Date by using current date as fallback
  const isValidDate = (date) => date instanceof Date && !isNaN(date.getTime());
  const defaultStartDate = isValidDate(importFormData?.startDate) ? importFormData?.startDate : today;
  
  const defaultStartTime = importFormData?.startTime;
  const defaultTimeResolution = importFormData?.timeResolution;
  const defaultRange = importFormData?.range;
  const defaultTaxRate = importFormData?.taxRate;

  const grayIconStyle = useMemo(
    () => ({
      padding: "4px",
    }),
    [colorScheme],
  ); 
  const [displayLoader, setDisplayLoader] = useState(false);
  const [fileContent, setFileContent] = useState("");
  const [errors, setErrors] = useState({});
  // Track fields whose external errors have been cleared by user interaction
  const [clearedExternalFields, setClearedExternalFields] = useState({});

  const handleFieldChange = (field, value) => {
    // propagate value to parent
    handleDynamicImportChange(field, value);
    // Clear the dynamic graph data when any field changes (user must re-upload)
    setDynamicGraphData(null);
    // Clear local error for this field if present
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    // Suppress external error for this field going forward
    if (externalErrors && externalErrors[field]) {
      setClearedExternalFields((prev) => ({ ...prev, [field]: true }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!defaultStartDate) newErrors.startDate = t("grid.dynamicTariff.validation.startDateRequired");
    if (!defaultStartTime) newErrors.startTime = t("grid.dynamicTariff.validation.startTimeRequired");
    if (!defaultTimeResolution) newErrors.timeResolution = t("grid.dynamicTariff.validation.timeResolutionRequired");
    if (!defaultRange) newErrors.range = t("grid.dynamicTariff.validation.rangeRequired");
    if (importFormData?.taxRate === undefined || importFormData?.taxRate === null || importFormData?.taxRate === "") {
      newErrors.taxRate = t("grid.dynamicTariff.validation.taxRateRequired");
    } else if (isNaN(Number(importFormData?.taxRate))) {
      newErrors.taxRate = t("grid.dynamicTariff.validation.taxRateMustBeNumber");
    } else if (Number(importFormData?.taxRate) < 0) {
      newErrors.taxRate = t("grid.dynamicTariff.validation.taxRateNegative");
    }
    if (!fileContent) newErrors.file = t("grid.dynamicTariff.validation.fileRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const externalActiveErrors = externalErrors && Object.keys(externalErrors).length > 0
    ? Object.fromEntries(
        Object.entries(externalErrors).filter(([key]) => !clearedExternalFields[key])
      )
    : {};
  const mergedErrors = Object.keys(externalActiveErrors).length > 0 ? { ...externalActiveErrors, ...errors } : errors;

  const handleFileDownload = (type) => {
    setDisplayLoader(true);
    MGA_API.downloadImportTemplate(type)
      .then(({ data, fileName }) => {
        fileDownload(data, fileName || "Grid");
        displayError("success", t("grid.dynamicTariff.success.fileDownloaded"));
      })
      .catch((error) => {
        console.log("Error Downloading File:", error);
      })
      .finally(() => {
        setDisplayLoader(false);
      });
  };

  const handleFileInputChange = (file) => {
    if(!isValidFile(file)) {
      displayError('error', t("grid.dynamicTariff.error.unsupportedFileFormat"));
      setFileContent(null);
      return
    }
    if (!file) {
      setFileContent(null);
      return;
    }
    setFileContent(file);
    // Clear file related errors locally & externally
    setErrors((prev) => {
      if (!prev.file) return prev;
      const next = { ...prev };
      delete next.file;
      return next;
    });
    if (externalErrors && externalErrors.file) {
      setClearedExternalFields((prev) => ({ ...prev, file: true }));
    }
  };

  const handleGetProfileClick = async () => {
    if (!validate()) return;
    setDisplayLoader(true);
    const payload = {
      startDate: new Date(defaultStartDate),
      startTime: defaultStartTime,
      timeResolution: defaultTimeResolution,
      range: defaultRange,
      taxRate: defaultTaxRate,
      units: currency,
      componentType: "Grid",
      datasource: "Upload",
      tariffUploadType: "IMPORT",
    };
    const formData = new FormData();
    formData.append("file", fileContent);
    formData.append(
      "profileDetail",
      new Blob([JSON.stringify(payload)], { type: "application/json" })
    );

    try {
      await MGA_API.uploadAndFetchDynamicTariff(formData).then((res) => {
        if (Array.isArray(res)) {
          // Normalize to numbers where possible for charting
          const normalized = res.map((v) => (v === null || v === undefined ? null : Number(v)));
          setDynamicGraphData({profileFile: normalized, profileDetail: payload});
        } else {
          setDynamicGraphData(null);
        }
      });
      setDisplayLoader(false);
      displayError("success", t("grid.dynamicTariff.success.fileUploaded"));
    } catch (error) {
      setDisplayLoader(false);
      setFileContent(null);
      const errorMessage = error?.response?.data
        ? error?.response?.data
        : t("grid.dynamicTariff.error.uploadFailed");
      displayError("error", errorMessage?.message);
    }
  };

  return (
    <>
      <se-loading loading={displayLoader} />
      <Grid gutter="md" className="mt-2 mb-2">
        <Grid.Col span={3}>
          <DateInput
            {...getFieldAttributes("field-19")}
            data-id="field-19"
            label={t("grid.dynamicTariff.label.startDate")}
            clearable
            value={defaultStartDate}
            onChange={(value) => handleFieldChange("startDate", value || oneYearAgo)}
            placeholder={t("grid.dynamicTariff.placeholder.startDate")}
            valueFormat={projectDateFormat}
            required
            // minDate={minStartDate}
            maxDate={oneYearAgo}
            error={mergedErrors.startDate}
            rightSection={
              <div style={grayIconStyle}>
                <IconCalendar size="1.1rem" stroke={1.5} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            popoverProps={{
              withinPortal: true,
              position: "bottom-start",
            }}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <Select
            {...getFieldAttributes("field-20")}
            data-id="field-20"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("grid.dynamicTariff.label.startTime")}
            data={generateTimeSeries()}
            value={defaultStartTime}
            onChange={(value) => handleFieldChange("startTime", value ?? "00:00")}
            required
            error={mergedErrors.startTime}
            size="sm"
            name="timeResolutions"
            id="timeResolutions"
            withinPortal
            dropdownPosition="top"
            styles={{
              dropdown: {
                left: 0,
                width: "100px",
                maxWidth: "100px",
              },
            }}
            searchable
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <Select
            {...getFieldAttributes("field-21")}
            data-id="field-21"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("grid.dynamicTariff.label.timeResolution")}
            data={[
              { value: 15, label: "15" },
              { value: 30, label: "30" },
              { value: 60, label: "60" },
            ]}
            value={defaultTimeResolution}
            onChange={(value) => handleFieldChange("timeResolution", value ?? 15)}
            required
            error={mergedErrors.timeResolution}
            withinPortal
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <Select
            {...getFieldAttributes("field-22")}
            data-id="field-22"
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("grid.dynamicTariff.label.range")}
            data={[{ value: 1, label: t("grid.dynamicTariff.option.oneYear") }]}
            value={defaultRange}
            onChange={(value) => handleFieldChange("range", value ?? 1)}
            withinPortal
            required
            error={mergedErrors.range}
            searchable
          />
        </Grid.Col>
      </Grid>
      <Grid gutter="md" className="mt-2 mb-2">
        {/* <Grid.Col span={3}>
          <TextInput
            label="Tax Rate"
            required
            type="text"
            placeholder="Add Tax in Percentage"
            value={defaultTaxRate}
            onChange={(event) => handleFieldChange("taxRate", event.currentTarget.value)}
            error={mergedErrors.taxRate}
            rightSection={<div style={grayIconStyle}>%</div>}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col> */}
        <Grid.Col span={3} className="get-profile">
          <FileInput
            {...getFieldAttributes("field-23")}
            data-id="field-23"
            label={
              <>
                <div>
                  {t("grid.dynamicTariff.label.uploadFile")}
                  <span style={{ color: "#fa5252" }}> *</span>
                </div>
                <span
                  style={{
                    textDecoration: "underline",
                    textUnderlineOffset: "3px",
                    color: "#42b4e6",
                    cursor: "pointer",
                  }}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    handleFileDownload("grid");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleFileDownload("grid");
                    }
                  }}
                >
                  {t("grid.dynamicTariff.link.downloadTemplate")}
                </span>
              </>
            }
            placeholder={t("grid.dynamicTariff.placeholder.uploadFile")}
            // accept=".xls, .xlsx, .csv"
            accept={ALLOWED_EXTENSIONS.join(",")}
            value={fileContent || null}
            onChange={(file) => {
              handleFileInputChange(file);
              onChange?.();
            }}
            error={mergedErrors.file}
            rightSection={
              <div style={grayIconStyle}>
                <IconFileSearch size={18} />
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        <Grid.Col
          span={3}
          style={{
            display: "flex",
            alignItems:
              mergedErrors.taxRate || mergedErrors.file ? "center" : "flex-end",
          }}
        >
          <Button
            variant="filled"
            onClick={handleGetProfileClick}
            sx={{
              backgroundColor: "#626469",
              "&:hover": {
                backgroundColor: "#494b50",
              },
            }}
            disabled={Object.keys(mergedErrors).length > 0}
          >
            {t("grid.dynamicTariff.button.getProfile")}
          </Button>
        </Grid.Col>
      </Grid>

      {dynamicGraphData?.profileFile?.length > 0 ? (
        <TimeSeriesGraph
          dataAssset={dynamicGraphData?.profileFile}
          name={t("grid.dynamicTariff.chart.rate")}
          yAxisName={`${t("grid.dynamicTariff.chart.rate")} (${currency})`}
          chartStartDate={defaultStartDate}
          startTime={defaultStartTime}
          interval={defaultTimeResolution}
          embedHeight={400}
          showMinimap={true}
          minimapGap={24}
          fullViewport={false}
          legendLabels={{ electricalLoad: t("grid.dynamicTariff.chart.rate"), energy: null }}
          tooltipLabels={{ generic: t("grid.dynamicTariff.chart.rate") }}
        />
      ) : null}
    </>
  );
};
export default DynamicTariffImport;
